import { SchemaTypes } from '@allesauto/graphql';
import { SCREEN_BREAKPOINT, useScreenBreakpoint } from '@allesauto/utils/hooks';
import { DeliveryParcel32, Information32 } from '@carbon/icons-react';
import clsx from 'clsx';
import React, { FC } from 'react';
import { Maybe } from 'yup/lib/types';

import NextLink from '../nextLink';
import Price from '../price';
import useCarDetails from '../shoppingCarCard/useCarDetails';
import VehicleImage from '../vehicleImage/index';

export interface SectionHeaderListProps {
  image: (SchemaTypes.VehicleImage | SchemaTypes.ProductImage)[] | null;
  imagePlaceholder?: 'none' | 'delivery';
  headline: string;
  headlineStyle?: string;
  subline?: string;
  fixedPrice?: Maybe<number> | undefined;
  monthlyPrice?: number;
  type?: HeaderListType;
  displayInline?: boolean;
  priceSupText?: string;
  className?: string;
  isFallback?: boolean;
  fallbackImg?: string;
  vehicleIndex?: number;
  trackingIdentifier?: string;
  sublineWithInfoBox?: boolean;
  isWhiteLabel?: boolean;
  pdpLink?: string;
  productIsVehicle?: boolean;
  product?: SchemaTypes.Vehicle;
  emissionValue?: string;
  consumptionValue?: string;
}

export enum HeaderListType {
  HEADER = 'header',
  LIST = 'list',
}

const SectionHeaderList: FC<SectionHeaderListProps> = (
  props: SectionHeaderListProps
) => {
  const {
    image,
    imagePlaceholder = 'none',
    headline,
    subline,
    fixedPrice,
    monthlyPrice,
    displayInline,
    type = HeaderListType.HEADER,
    priceSupText,
    headlineStyle,
    className: customClassName,
    isFallback,
    fallbackImg,
    trackingIdentifier,
    vehicleIndex,
    sublineWithInfoBox = false,
    isWhiteLabel = false,
    pdpLink,
    productIsVehicle,
    product,
    emissionValue,
    consumptionValue,
  } = props;

  const breakpoint = useScreenBreakpoint();
  const smallDevice = breakpoint === SCREEN_BREAKPOINT.SM;
  const isListComponent = type === HeaderListType.LIST;
  const isInline = displayInline || !smallDevice;

  const carDetails = useCarDetails({
    product,
    consumptionValue,
    emissionValue,
  });

  const className = {
    container: clsx(
      'aurego-border-bottom bg-white max-w-screen-xl w-full',
      'h-auto',
      isInline && 'grid grid-cols-24',
      isInline && smallDevice && 'content-center',
      !isInline &&
        subline &&
        'sm:h-auto sm:grid-rows-2 sm:grid-flow-col NOTINLINE',
      !isInline && !subline && 'sm:h-auto',
      isListComponent && 'h-auto py-0',
      isListComponent &&
        subline &&
        smallDevice &&
        !displayInline &&
        'sm:h-auto sm:grid-rows-2 sm:grid-flow-col',
      isListComponent && !subline && smallDevice && 'sm:h-auto',
      customClassName
    ),
    imageContainer: clsx(
      'col-span-5 h-full md:col-span-3 lg:col-span-2 ',
      !isInline && 'sm:h-1/2 sm:grid sm:grid-cols-24',
      isListComponent && 'md:inline-grid'
    ),
    imageStyle: clsx(
      'flex items-center pointer-events-none',
      !isInline && 'sm:col-span-5 sm:h-[50px]'
    ),
    imageStylePlaceholder: clsx(
      'flex items-center justify-center text-purple80 wl-text-brand',
      !isInline && 'sm:col-span-5 sm:h-[50px]'
    ),
    lineContainer: clsx(
      'col-start-auto',
      isInline &&
        'col-span-19 md:col-span-21 lg:col-span-22 grid grid-cols-[_1fr_min-content] content-center',
      !isInline && 'sm:grid sm:grid-cols-2 sm:mt-10p col-start-1',
      isInline && 'ml-10px md:ml-15px sm:py-0 md:py-15px'
    ),
    headlineTextStyle: clsx(
      'font-headline leading-5 text-grey100 sm:pt-10px md:pt-0',
      !isInline && !headlineStyle && 'truncate',
      headlineStyle && headlineStyle
    ),
    headlineContainerStyle: clsx(
      isInline &&
        fixedPrice === undefined &&
        monthlyPrice === undefined &&
        'col-span-full',
      !isInline && 'sm:inline-grid col-span-2'
    ),
    sublineStyle: clsx(
      'col-span-2 font-regular leading-5 text-grey100 text-p2 sm:pt-5px',
      sublineWithInfoBox && 'lg:pt-10px'
    ),
    priceContainerStyle: clsx(
      ' text-grey100 text-right',
      !subline && isInline && 'col-span-auto',
      !isInline && 'sm:col-start-18 sm:col-span-6'
    ),
    pricePrefixStyle: clsx('font-regular leading-5 mr-5px'),
    priceValueStyle: clsx(
      '!text-grey100 font-headline leading-5 sm:pt-10px sm:text-p1 md:pt-0'
    ),
  };

  const getImage = (
    images: (SchemaTypes.VehicleImage | SchemaTypes.ProductImage)[]
  ) => {
    const noImage = undefined;
    if (!images || images.length < 1) return isFallback ? fallbackImg : noImage;

    if (
      image &&
      'externalUrlLarge' in image[0] &&
      image[0].externalUrlLarge !== '/'
    )
      return image[0].externalUrlLarge;

    if (image && 'url' in image[0] && image[0].url !== '/') return image[0].url;

    return noImage;
  };

  const placeholderIcon = {
    delivery: <DeliveryParcel32 />,
    none: (
      <VehicleImage
        filename={undefined}
        fallbackImage="pcp_pdp"
        isWhiteLabel={isWhiteLabel}
      />
    ),
  };

  const {
    container,
    imageStyle,
    imageStylePlaceholder,
    headlineTextStyle,
    sublineStyle,
    priceContainerStyle,
    priceValueStyle,
    lineContainer,
    headlineContainerStyle,
    imageContainer,
  } = className;

  const trackingIdentifierVehicle = `${trackingIdentifier}--card-vehicle${vehicleIndex}`;

  const parsedImage = getImage(image ?? []);

  return (
    <div className={container}>
      <div className={imageContainer}>
        {!parsedImage ? (
          productIsVehicle ? (
            <NextLink href={pdpLink as string} className="no-underline">
              <div className={imageStylePlaceholder}>
                {placeholderIcon[imagePlaceholder]}
              </div>
            </NextLink>
          ) : (
            <div className={imageStylePlaceholder}>
              {placeholderIcon[imagePlaceholder]}
            </div>
          )
        ) : productIsVehicle ? (
          <NextLink href={pdpLink as string} className="no-underline">
            <div className={imageStyle}>
              <VehicleImage
                aspectRatio="4_BY_3"
                filename={parsedImage}
                isWhiteLabel={isWhiteLabel}
              />
            </div>
          </NextLink>
        ) : (
          <div className={imageStyle}>
            <VehicleImage
              aspectRatio="4_BY_3"
              filename={parsedImage}
              isWhiteLabel={isWhiteLabel}
            />
          </div>
        )}
      </div>
      <div className={lineContainer}>
        <div className={headlineContainerStyle}>
          <span
            className={headlineTextStyle}
            data-tracking-id={trackingIdentifierVehicle}
          >
            {headline}
          </span>
        </div>
        {(fixedPrice !== undefined || monthlyPrice) && (
          <div className={priceContainerStyle}>
            {fixedPrice !== undefined && (
              <Price
                price={fixedPrice}
                priceTextKey={'ui.PRICE.PRICE_ONLY'}
                classNames={{
                  mainPriceClass: priceValueStyle,
                }}
                hasFractionDigits={true}
              />
            )}
            {!!monthlyPrice && (
              <Price
                price={null}
                classNames={{
                  monthlyPriceClass: priceValueStyle,
                  monthlyPriceTextClass: priceValueStyle,
                }}
                monthlyPrice={monthlyPrice}
                monthlyTextKey={'ui.PRICE.MONTHLY_SHORT'}
                priceSupText={priceSupText}
                hasFractionDigits={true}
              />
            )}
          </div>
        )}
        {sublineWithInfoBox ? (
          <div className={sublineStyle}>
            <Information32
              className={'h-[14px] w-[14px] mb-[1px] inline-block'}
            />
            <span className="align-top ml-[6px]">{subline}</span>
          </div>
        ) : (
          subline && (
            <div
              dangerouslySetInnerHTML={{ __html: subline }}
              className={sublineStyle}
            ></div>
          )
        )}
      </div>
      <div className={'grid lg:col-start-4  md:mt-[16px]'}>
        {productIsVehicle &&
          carDetails &&
          carDetails.map(({ label, value = '' }, index) => {
            const half = Math.ceil(carDetails.length / 2);

            return (
              <React.Fragment key={label}>
                <p
                  className={`text-black text-p2 sm:pr-10px lg:pr-[50px] sm:mt-5px  whitespace-nowrap sm:grid md:row-start-${
                    (index % half) + 1
                  } !sm:col-start-${index < half ? 1 : 3} 
                       lg:row-start-${(index % half) + 1} 
                        lg:col-start-${index < half ? 1 : 3}`}
                >
                  {label}
                </p>
                <p
                  className={`text-grey50 text-p2 sm:mt-5px md:pr-[40px] lg:pr-[50px] whitespace-nowrap md:grid md:row-start-${
                    (index % half) + 1
                  } !md:col-start-${index < half ? 2 : 4}
                      lg:grid lg:row-start-${(index % half) + 1} lg:col-start-${
                    index < half ? 2 : 4
                  }`}
                >
                  {value}
                </p>
              </React.Fragment>
            );
          })}
      </div>
    </div>
  );
};

export default SectionHeaderList;
